import React, { useCallback, useState } from 'react';
import { Clickable, Stack, Text, Select } from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { ModalOverlay } from '../../Modal';
import { useHolidayCutoff, Region } from './useHolidayCutoff';

import './HolidayDelayModal.scss';

interface Props {
  show: boolean;
  onClose: () => void;
}

type RegionOption = {
  label: string;
  value: Region | '';
};

const REGIONS: RegionOption[] = [
  { label: 'Select Region', value: '' },
  { label: 'United States (West)', value: 'west' },
  { label: 'United States (Midwest)', value: 'midwest' },
  { label: 'United States (East)', value: 'east' },
  { label: 'Asia and Middle East', value: 'asia' },
  { label: 'Europe', value: 'europe' }
];

export const HolidayDelayModal: React.FC<Props> = ({ show, onClose }) => {
  const cutoff = useHolidayCutoff();

  const [selectedRegion, setSelectedRegion] = useState(REGIONS[0]);

  const handleCountryChange = useCallback(
    (value: string) => {
      const foundRegion = REGIONS.find(region => region.value === value);
      setSelectedRegion(foundRegion || REGIONS[0]);
    },
    [setSelectedRegion]
  );

  if (!cutoff) return null;

  return (
    <>
      <ModalOverlay onClose={onClose} show={show}>
        <div className="HolidayDelayModal">
          <Stack className="HolidayDelayModal__top" alignItems="center" space={3}>
            <Clickable className="HolidayDelayModal__close" aria-label="Close" onClick={onClose}>
              <ExitIcon />
            </Clickable>
            <Text className="HolidayDelayModal__title" family="serif" treatment="h5">
              Holiday Shipping Cutoff Dates
            </Text>
          </Stack>
          <Stack className="HolidayDelayModal__content-text" alignItems="center" space={4}>
            <Text className="HolidayDelayModal__arrival-date" treatment="bold1">
              {cutoff?.arrivalDate} Arrival Date
            </Text>
            <Text className="HolidayDelayModal__description">
              Select your region to see the shipping cut off dates for shipping types. We suggest
              placing your order before 11AM EST for all cut off dates to ensure timely delivery.
            </Text>

            <Select
              className="HolidayDelayModal__select"
              onChange={handleCountryChange}
              options={REGIONS}
              value={selectedRegion.value}
            />

            <Stack className="HolidayDelayModal__content-table" alignItems="center" space={1}>
              <table className="HolidayDelayModal__table" aria-describedby="Holiday Table">
                <thead className="HolidayDelayModal__thead">
                  <tr>
                    <th className="HolidayDelayModal__th" scope="col">
                      <Text className="HolidayDelayModal__region" treatment="body2">
                        STANDARD
                      </Text>
                    </th>

                    <th className="HolidayDelayModal__th" scope="col">
                      <Text className="HolidayDelayModal__region" treatment="body2">
                        2 DAY EXPRESS
                      </Text>
                    </th>
                    <th className="HolidayDelayModal__th" scope="col">
                      <Text className="HolidayDelayModal__region" treatment="body2">
                        OVERNIGHT
                      </Text>
                    </th>
                  </tr>
                </thead>
                <tbody className="HolidayDelayModal__tbody">
                  <tr className="HolidayDelayModal__tr-middle">
                    <>
                      <td className="HolidayDelayModal__td-first">
                        <Text className="HolidayDelayModal__region" treatment="body2">
                          {selectedRegion.value === ''
                            ? ''
                            : cutoff.cutoffDates[selectedRegion.value].standard}
                        </Text>
                      </td>
                      <td className="HolidayDelayModal__td-first">
                        <Text className="HolidayDelayModal__region" treatment="body2">
                          {selectedRegion.value === ''
                            ? ''
                            : cutoff.cutoffDates[selectedRegion.value].express}
                        </Text>
                      </td>
                      <td className="HolidayDelayModal__td-first">
                        <Text className="HolidayDelayModal__region" treatment="body2">
                          {selectedRegion.value === ''
                            ? ''
                            : cutoff.cutoffDates[selectedRegion.value].overnight}
                        </Text>
                      </td>
                    </>
                  </tr>
                </tbody>
              </table>
            </Stack>
          </Stack>
        </div>
      </ModalOverlay>
    </>
  );
};
